import { useRouter } from 'next/router';
import React, { FC, useEffect, useState } from 'react';

import categoryAPI from 'src/api/category';
import learningPathAPI from 'src/api/learningPath';

import CourseDetailSection from '@components/templates/courseDetail/courseDetailSection';
import { useCourseDiscussionList } from '@hooks/course/courseDiscussion/useCourseDiscussionList';
import { useCourseReview } from '@hooks/course/courseReview/useCourseReview';
import useCourseDetail from '@hooks/course/useCourseDetail';
import useCourseEnrollment from '@hooks/course/useCourseEnrollment';
import { CourseEntity } from '@redux/course/type/courseDetailType';

type CourseDetailProps = {
  courseDetailSeo: CourseEntity;
};

const CourseDetail: FC<CourseDetailProps> = ({ courseDetailSeo }) => {
  const router = useRouter();

  const courseIdOrIdName = router?.query?.idName as string;
  const learningPathId = router?.query?.learningPathId as string;
  const learningPathCategorySlug = router?.query?.category as string;
  const courseCount = router?.query?.courseCount as string;
  const { id: courseId } = courseDetailSeo;
  const { handleFetchCourseDetail, handleResetCourseDetail } = useCourseDetail();
  const { handleResetCourseDiscussion } = useCourseDiscussionList({ idName: courseIdOrIdName });
  const { handleResetCourseReview } = useCourseReview({ idName: courseIdOrIdName });
  const { handleFetchCourseEnrollment, handleResetCourseEnrollment } = useCourseEnrollment();
  const [learningPathData, setLearningPathData] = useState(undefined);
  const [learningPathCategoryData, setLearningPathCategoryData] = useState(undefined);

  useEffect(() => {
    if (!learningPathId || !learningPathCategorySlug) {
      return;
    }

    const fetchLearningPath = async () => {
      try {
        const responseLearningPath = await learningPathAPI.fetchLearningPath(parseInt(learningPathId, 10));
        if (responseLearningPath.data) {
          setLearningPathData(responseLearningPath.data);
        } else {
          router.push('/not-found');
        }
      } catch (error) {
        router.push('/not-found');
      }
    };

    fetchLearningPath();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learningPathId]);

  useEffect(() => {
    if (!learningPathCategorySlug) {
      return;
    }

    const fetchLearningPathCategory = async () => {
      try {
        const responseCategory = await categoryAPI.fetchCategory(learningPathCategorySlug);
        if (responseCategory.data) {
          setLearningPathCategoryData(responseCategory.data);
        } else {
          router.push('/404');
        }
      } catch (error) {
        router.push('/404');
      }
    };

    fetchLearningPathCategory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learningPathCategorySlug]);

  useEffect(() => {
    if (!courseIdOrIdName) {
      return;
    }
    handleFetchCourseDetail(courseIdOrIdName as string);
    return () => {
      handleResetCourseDetail();
      handleResetCourseEnrollment();
      handleResetCourseReview();
      handleResetCourseDiscussion();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseIdOrIdName]);

  useEffect(() => {
    if (!courseId) {
      return;
    }
    handleFetchCourseEnrollment(courseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  return (
    <>
      <CourseDetailSection
        courseDetailSeo={courseDetailSeo}
        learningPath={learningPathData}
        learningPathCategory={learningPathCategoryData}
        courseCount={courseCount}
      />
    </>
  );
};

export default CourseDetail;
